import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { RichTextEditor } from "@/components/shared/RichTextEditor";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type KickoffRecipient,
  useKickoffEmailPreview,
  useSendKickoffEmail,
} from "@/hooks/use-audits";
import { isRichTextEmpty } from "@/lib/rich-text";

interface KickoffEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  auditId: string;
  /** Send the email again after it was already sent (admins only). */
  resend?: boolean;
}

function RecipientList({ label, people }: { label: string; people: KickoffRecipient[] }) {
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium leading-none">{label}</p>
      {people.length === 0 ? (
        <p className="text-sm text-muted-foreground">None</p>
      ) : (
        <ul className="space-y-0.5 text-sm">
          {people.map((p) => (
            <li key={p.email}>
              {p.name ?? p.role} <span className="text-muted-foreground">({p.role})</span>{" "}
              <span className="text-muted-foreground">&lt;{p.email}&gt;</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function KickoffEmailDialog({
  open,
  onOpenChange,
  auditId,
  resend = false,
}: KickoffEmailDialogProps) {
  const { data, isLoading, isError, refetch } = useKickoffEmailPreview(auditId, open, resend);
  const send = useSendKickoffEmail(auditId, resend);

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  // Pre-fill from the template whenever the pop-up (re)opens; edits after that win.
  useEffect(() => {
    if (open && data) {
      setSubject(data.subject);
      setBody(data.body);
    }
  }, [open, data]);

  async function handleSend() {
    try {
      await send.mutateAsync({ subject, body });
      onOpenChange(false);
    } catch {
      // The mutation's onError already shows the toast; keep the dialog open to retry.
    }
  }

  const noViableTo = data !== undefined && data.to.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{resend ? "Resend Kickoff Email" : "Send Kickoff Email"}</DialogTitle>
          <DialogDescription>
            {resend
              ? "This audit's kickoff email was already sent. Sending again emails everyone below a second time and replaces the record of the first send."
              : "This can only be sent once for this audit. Review the recipients, and edit the subject or message if needed."}
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : isError || !data ? (
          <ErrorState message="Failed to load the kickoff email" onRetry={() => refetch()} />
        ) : (
          <div className="space-y-4">
            <RecipientList label="To" people={data.to} />
            <RecipientList label="CC" people={data.cc} />
            {data.missing.length > 0 && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                <p className="flex items-center gap-1 font-medium">
                  <AlertTriangle className="h-4 w-4" />
                  {noViableTo
                    ? "Can't send: nobody on the To line has an email address"
                    : "These people won't receive it"}
                </p>
                <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                  {data.missing.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              </div>
            )}
            {data.guidanceHoursMissing && (
              <p className="text-sm text-muted-foreground">
                Guidance hours weren't recorded for this audit, so that placeholder is blank.
              </p>
            )}
            {data.ineligibleReason && (
              <p className="text-sm text-destructive">{data.ineligibleReason}</p>
            )}
            <div className="space-y-1">
              <p className="text-sm font-medium leading-none">Subject</p>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium leading-none">Message</p>
              <RichTextEditor ariaLabel="Message" value={body} onChange={setBody} />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!data?.canSend || !subject.trim() || isRichTextEmpty(body) || send.isPending}
            onClick={handleSend}
          >
            {send.isPending ? "Sending…" : resend ? "Resend" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
